import dayjs from 'dayjs';
import _ from 'lodash';
import { useMemo } from 'react';
import {
  CubeDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
} from '../../../../../services/data/impl/cube/cubeSlice';
import { UnitsEnum } from '../../../../../services/entities/impl/blfApi/apiConstants';
import { Block } from '../../../../../services/entities/impl/blfApi/schemas/block';
import {
  CropTypes,
  Variety,
} from '../../../../../services/entities/impl/blfApi/schemas/variety';
import {
  CustomerSummaryFilterEnum,
  useFilterCustomerSummary,
} from '../../../../customerSummary/hooks/useFilterCustomerSummary';
import calculateCountPerPlant from '../../../../dataMesh/utils/calculatePerPlant';
import { useUnits } from '../../../../settings/hooks/useUnits';
import { RanchSummaryTableMeasureEnum } from '../../../dataGridTablesSlice';
import { MISSING_DATA_PLACEHOLDER } from '../../../GenericDataGrid';
import { convertAndRound } from '../../../utils/convertAndRound';
import { roundSafe } from '../../../utils/roundSafe';
import useCreateRanchSummaryTableDateRange from '../../hooks/useCreateRanchSummaryTableDateRange';
import { GrapeColumnEnum } from '../RanchSummaryTableGrape';

const DEFAULT_ROUND_DIGITS = 2;
const COUNT_ROUND_DIGITS = 0;

type NumType = number | null | undefined;

export enum GrapeMeasureEnum {
  perMeter = 'perMeter',
  perPlant = 'perPlant',
  totalCount = 'totalCount',
  length = 'length',
}

export interface GrapeMeasureValues {
  [GrapeMeasureEnum.perMeter]: NumType;
  [GrapeMeasureEnum.perPlant]: NumType;
  [GrapeMeasureEnum.totalCount]: NumType;
  [GrapeMeasureEnum.length]?: NumType;
}

export interface RanchSummaryTableGrapeRow {
  id: number;
  scanID: string;
  [GrapeColumnEnum.scanDate]: string;
  [GrapeColumnEnum.ranchName]: string;
  ranchID: string;
  [GrapeColumnEnum.blockName]: string;
  blockID: string;
  [GrapeColumnEnum.variety]: string;
  [GrapeColumnEnum.buds]: GrapeMeasureValues;
  [GrapeColumnEnum.earlyShoots]: GrapeMeasureValues;
  [GrapeColumnEnum.shoots]: GrapeMeasureValues;
  [GrapeColumnEnum.earlyClusters]: GrapeMeasureValues;
  [GrapeColumnEnum.clusters]: GrapeMeasureValues;
  [GrapeColumnEnum.berries]: GrapeMeasureValues;
}

export const SelectedMeasureMap: Record<
  RanchSummaryTableMeasureEnum,
  GrapeMeasureEnum
> = {
  [RanchSummaryTableMeasureEnum.perMeter]: GrapeMeasureEnum.perMeter,
  [RanchSummaryTableMeasureEnum.perPlant]: GrapeMeasureEnum.perPlant,
  [RanchSummaryTableMeasureEnum.totalCount]: GrapeMeasureEnum.totalCount,
};

/**
 * Generates rows for the Ranch Summary Table for grapes.
 *
 * @param {Block[] | undefined} blocksFiltered - The filtered blocks to generate the table rows for.
 * @param {Variety[] | undefined} varieties - The varieties to lookup variety names from.
 * @returns {(RanchSummaryTableGrapeRow | null)[]} The generated rows for the Ranch Summary Table.
 */
const useGenerateRanchSummaryTableGrapeRows = (
  blocksFiltered: Block[] | undefined,
  varieties: Variety[] | undefined
) => {
  const units = useUnits();
  const { dateRange } = useCreateRanchSummaryTableDateRange();

  const filteredCustomerSummary = useFilterCustomerSummary({
    activeFilters: [
      CustomerSummaryFilterEnum.ranch,
      CustomerSummaryFilterEnum.block,
      CustomerSummaryFilterEnum.highestAIServiceVersion,
    ],
    cropTypes: [
      CropTypes.JuiceGrapes,
      CropTypes.TableGrapes,
      CropTypes.WineGrapes,
      CropTypes.VineNursery,
    ],
    dateRange: dateRange,
  });

  const rows: (RanchSummaryTableGrapeRow | null)[] = useMemo(() => {
    const rowsResult: RanchSummaryTableGrapeRow[] = [];

    if (!blocksFiltered || !filteredCustomerSummary) {
      return [];
    }

    // Create variety lookup map for performance
    const varietyMap = new Map(varieties?.map((v) => [v.id, v]) || []);

    // First group the individual customer summary detection fact entries by scanID.  For example, if a particular scan has multiple detections such as buds, shoots, etc, they will be grouped together in one scan group.
    const scanGroup = _.groupBy(
      filteredCustomerSummary,
      CubeDimensionEnum.detectionFactScanId
    );

    // Loop through each scan group and build a row compiling all the different AI detections for that scan into a single row.
    Object.entries(scanGroup).forEach(([scanId, detections], index) => {
      const firstDetection = detections[0];
      const convertToImperial = units === UnitsEnum.imperial;

      // Find the block that corresponds to the scan group
      const block = blocksFiltered?.find(
        (block) =>
          block.id === firstDetection?.[CubeDimensionEnum.detectionFactBlockId]
      );

      // If the block is not found, skip this entry
      if (!block) {
        return;
      }

      // Create a new row object
      const currentRow: RanchSummaryTableGrapeRow = {
        id: index,
        scanDate: dayjs(firstDetection[CubeDimensionEnum.scanDate]).format(
          'MM/DD/YYYY'
        ),
        scanID: scanId,
        ranchName: firstDetection[CubeDimensionEnum.ranchName],
        ranchID: firstDetection[CubeDimensionEnum.detectionFactRanchId],
        blockName: firstDetection[CubeDimensionEnum.blockName],
        blockID: firstDetection[CubeDimensionEnum.detectionFactBlockId],
        variety:
          varietyMap.get(block.varietyID)?.name ?? MISSING_DATA_PLACEHOLDER,
        buds: {
          perMeter: null,
          perPlant: null,
          totalCount: null,
        },
        earlyShoots: {
          perMeter: null,
          perPlant: null,
          totalCount: null,
        },
        shoots: {
          perMeter: null,
          perPlant: null,
          totalCount: null,
          length: null,
        },
        earlyClusters: {
          perMeter: null,
          perPlant: null,
          totalCount: null,
        },
        clusters: {
          perMeter: null,
          perPlant: null,
          totalCount: null,
        },
        berries: {
          perMeter: null,
          perPlant: null,
          totalCount: null,
          length: null,
        },
      };

      // Loop through each detection fact entry for the scan group and generate a row for the table by compiling all the different AI detections for that scan into a single row.
      detections.forEach((detection) => {
        const className = detection[CubeDimensionEnum.className];
        const estimatedCountRaw =
          detection[CubeMeasureEnum.detectionFactEstimatedCount];
        const distanceTraveledRaw =
          detection[CubeMeasureEnum.detectionFactTraveledDistance];

        const perMeter = convertAndRound(
          detection[CubeMeasureEnum.detectionFactAvgCountPerMeter],
          convertToImperial,
          'Feet',
          true,
          DEFAULT_ROUND_DIGITS
        );

        const totalCount = roundSafe(estimatedCountRaw, COUNT_ROUND_DIGITS);

        const perPlantRaw = calculateCountPerPlant(
          estimatedCountRaw,
          1,
          block.plantSpacing,
          distanceTraveledRaw,
          'Meters'
        );

        const perPlant = roundSafe(perPlantRaw, DEFAULT_ROUND_DIGITS);

        switch (className) {
          case FeatureEnum.bud:
            currentRow.buds.perMeter = perMeter;
            currentRow.buds.perPlant = perPlant;
            currentRow.buds.totalCount = totalCount;
            break;
          case FeatureEnum.earlyShoot:
            currentRow.earlyShoots.perMeter = perMeter;
            currentRow.earlyShoots.perPlant = perPlant;
            currentRow.earlyShoots.totalCount = totalCount;
            break;
          case FeatureEnum.shoot:
            currentRow.shoots.perMeter = perMeter;
            currentRow.shoots.perPlant = perPlant;
            currentRow.shoots.totalCount = totalCount;
            currentRow.shoots.length = convertAndRound(
              detection[CubeMeasureEnum.DetectionFactAvgLength],
              true,
              convertToImperial ? 'Inches' : 'Millimeters',
              false,
              DEFAULT_ROUND_DIGITS
            );
            break;
          case FeatureEnum.earlyCluster:
            currentRow.earlyClusters.perMeter = perMeter;
            currentRow.earlyClusters.perPlant = perPlant;
            currentRow.earlyClusters.totalCount = totalCount;
            break;
          case FeatureEnum.clusters:
            currentRow.clusters.perMeter = perMeter;
            currentRow.clusters.perPlant = perPlant;
            currentRow.clusters.totalCount = totalCount;
            break;
          case FeatureEnum.berries:
            currentRow.berries.perMeter = perMeter;
            currentRow.berries.perPlant = perPlant;
            currentRow.berries.totalCount = totalCount;
            currentRow.berries.length = convertAndRound(
              detection[CubeMeasureEnum.DetectionFactAvgLength],
              true,
              'Millimeters', // Always convert to mm, industry standard
              false,
              DEFAULT_ROUND_DIGITS
            );
            break;
          default:
            break;
        }
      });

      rowsResult.push(currentRow);
    });

    return rowsResult;
  }, [blocksFiltered, filteredCustomerSummary, units, varieties]);

  return rows;
};

export default useGenerateRanchSummaryTableGrapeRows;
